import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, Skeleton, Divider } from '@mui/material';
import { Public as SystemIcon, Business as StationIcon, Security as SecurityIcon, TrendingUp as EconomyIcon, People as PopulationIcon } from '@mui/icons-material';
import { DATABASE_SERVICE_URL } from '../../services/workerUrls';
import { logService } from '../../services/logService';
import type { Player, StarSystem, Station } from '../../types/gameState';

interface LocationPanelProps {
  playerId: string;
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  color?: string;
}

function InfoRow({ icon, label, value, color }: InfoRowProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 500, color: color || 'text.primary', textTransform: 'capitalize' }}>
        {value || 'Unknown'}
      </Typography>
    </Box>
  );
}

export function LocationPanel({ playerId }: LocationPanelProps) {
  const [player, setPlayer] = useState<Player | null>(null);
  const [system, setSystem] = useState<StarSystem | null>(null);
  const [station, setStation] = useState<Station | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchLocation() {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch player first to get location IDs
        const playerLogId = logService.api('GET', `/api/players/${playerId}`, {}, 'pending');
        const playerStart = Date.now();
        const playerRes = await fetch(`${DATABASE_SERVICE_URL()}/api/players/${playerId}`);
        const playerDuration = Date.now() - playerStart;

        if (!playerRes.ok) {
          logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: `HTTP ${playerRes.status}` });
          setError('Failed to load player data');
          setIsLoading(false);
          return;
        }

        const playerResult = await playerRes.json();
        if (!playerResult.success || !playerResult.data) {
          logService.updateLog(playerLogId, { status: 'error', duration: playerDuration, message: 'No player data' });
          setError('No player data available');
          setIsLoading(false);
          return;
        }

        logService.updateLog(playerLogId, { status: 'success', duration: playerDuration });
        setPlayer(playerResult.data);

        // Fetch system
        if (playerResult.data.current_system_id) {
          const systemLogId = logService.api('GET', `/api/systems/${playerResult.data.current_system_id}`, {}, 'pending');
          const systemStart = Date.now();
          const systemRes = await fetch(`${DATABASE_SERVICE_URL()}/api/systems/${playerResult.data.current_system_id}`);
          const systemDuration = Date.now() - systemStart;

          if (systemRes.ok) {
            const systemResult = await systemRes.json();
            if (systemResult.success && systemResult.data) {
              logService.updateLog(systemLogId, { status: 'success', duration: systemDuration });
              setSystem(systemResult.data);
            } else {
              logService.updateLog(systemLogId, { status: 'error', duration: systemDuration, message: 'No system data' });
            }
          } else {
            logService.updateLog(systemLogId, { status: 'error', duration: systemDuration, message: `HTTP ${systemRes.status}` });
          }
        }

        // Fetch station if docked
        if (playerResult.data.current_station_id) {
          const stationLogId = logService.api('GET', `/api/stations/${playerResult.data.current_station_id}`, {}, 'pending');
          const stationStart = Date.now();
          const stationRes = await fetch(`${DATABASE_SERVICE_URL()}/api/stations/${playerResult.data.current_station_id}`);
          const stationDuration = Date.now() - stationStart;

          if (stationRes.ok) {
            const stationResult = await stationRes.json();
            if (stationResult.success && stationResult.data) {
              logService.updateLog(stationLogId, { status: 'success', duration: stationDuration });
              setStation(stationResult.data);
            } else {
              logService.updateLog(stationLogId, { status: 'error', duration: stationDuration, message: 'No station data' });
            }
          } else {
            logService.updateLog(stationLogId, { status: 'error', duration: stationDuration, message: `HTTP ${stationRes.status}` });
          }
        }
      } catch (err) {
        setError('Failed to load location data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchLocation();
  }, [playerId]);

  if (isLoading) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Skeleton variant="text" width="70%" height={24} />
              <Skeleton variant="text" width="50%" height={20} />
              <Skeleton variant="rectangular" height={100} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="panel">
        <Box className="panel-scroll">
          <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="body2" color="error" align="center">
                {error}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="panel">
      <Box className="panel-scroll">
        <Card sx={{ backgroundColor: 'background.paper', border: 1, borderColor: 'divider' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 1, fontSize: '0.75rem', display: 'block', mb: 1 }}>
              Current Location
            </Typography>

            {/* Credits */}
            {player && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 1.5, borderRadius: 1, backgroundColor: 'action.hover' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Credits
                </Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'success.main' }}>
                  ¤{player.credits.toLocaleString()}
                </Typography>
              </Box>
            )}

            {/* System Info */}
            {system && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SystemIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {system.name}
                  </Typography>
                </Box>
                <InfoRow icon={<SecurityIcon sx={{ fontSize: 16 }} />} label="Security" value={system.security_level} />
                <InfoRow icon={<EconomyIcon sx={{ fontSize: 16 }} />} label="Economy" value={system.economy_type} />
                <InfoRow icon={<PopulationIcon sx={{ fontSize: 16 }} />} label="Population" value={system.population_class} />
              </>
            )}

            {/* Station Info */}
            {station && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <StationIcon sx={{ color: 'info.main', fontSize: 20 }} />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {station.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {station.station_type}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                  {!!station.has_market && <Chip label="Market" size="small" color="success" variant="outlined" />}
                  {!!station.has_shipyard && <Chip label="Shipyard" size="small" color="info" variant="outlined" />}
                  {!!station.has_repair && <Chip label="Repairs" size="small" color="warning" variant="outlined" />}
                </Box>
              </>
            )}

            {!system && !station && (
              <Typography variant="body2" color="text.secondary" align="center">
                Location unknown
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
